<template>
  <div class="dropdown b-dropdown btn-group"
       :class="[directionClass, { show: visible }]"
       :id="id">
    <component :is="toggleTag"
               v-bind="toggleAttrs"
               class="btn dropdown-toggle"
               :class="[`btn-${variant}`, toggleClass, { 'dropdown-toggle-no-caret': noCaret }]"
               :id="toggleId"
               aria-haspopup="menu"
               :aria-expanded="visible ? 'true' : 'false'"
               ref="toggler"
               @mousedown="onMousedown"
               @click="toggle"
               @keydown="toggle"><slot name="button-content">{{ text }}</slot></component>
    <ul class="dropdown-menu"
        :class="[menuClass, { 'dropdown-menu-right': right, show: visible }]"
        role="menu"
        tabindex="-1"
        :aria-labelledby="toggleId"
        ref="menu"
        @keydown="onKeydown"><slot :hide="hide"/></ul>
  </div>
</template>

<script setup>
// Replaces bootstrap-vue's <b-dropdown> with the same markup and behaviour (only what the app uses, see
// __tests__/bootstrap/bDropdown.test.js). Reference: node_modules/bootstrap-vue/src/components/dropdown/dropdown.js
// and .../mixins/dropdown.js
// popper.js v1 with bootstrap-vue's settings, so the menu lands on the same pixels (see vBTooltip.js)
import Popper from "popper.js"
import { computed, nextTick, onBeforeUnmount, provide, ref, watch } from "vue"
import { nextId } from "@/components/bootstrap/uid"
import { dropdownOpened, onOtherDropdownOpened } from "@/components/bootstrap/dropdownRegistry"

defineOptions({
  name: 'BDropdown',
})

const props = defineProps({
  variant: {
    type: String,
    default: 'secondary'
  },
  text: {
    type: String,
    default: null
  },
  noCaret: {
    type: Boolean,
    default: false
  },
  // the menu is aligned to the right edge of the toggle
  right: {
    type: Boolean,
    default: false
  },
  // the menu opens to the left of the toggle
  dropleft: {
    type: Boolean,
    default: false
  },
  toggleTag: {
    type: String,
    default: 'button'
  },
  toggleClass: {
    type: [String, Array, Object],
    default: null
  },
  menuClass: {
    type: [String, Array, Object],
    default: null
  },
  // merged over the popper.js options
  popperOpts: {
    type: Object,
    default: () => ({})
  }
})

const emit = defineEmits(['show', 'shown', 'hide', 'hidden', 'toggle'])

const id = nextId('dropdown')
const toggleId = `${id}__BV_toggle_`

const visible = ref(false)
const toggler = ref(null)
const menu = ref(null)

let popper = null
let hideTimeout = null
let visibleChangePrevented = false
let focusTogglerOnHidden = false
let stopListeningToOthers = null

const directionClass = computed(() => (props.dropleft ? 'dropleft' : ''))

// as bootstrap-vue renders it: a toggle that is not a <button> is a placeholder link (the click never navigates)
const toggleAttrs = computed(() => {
  if(props.toggleTag === 'button') return { type: 'button' }
  return { href: '#', target: '_self' }
})

const popperConfig = () => {
  let placement = 'bottom-start'
  if(props.dropleft) placement = 'left-start'
  else if(props.right) placement = 'bottom-end'
  const config = {
    placement,
    modifiers: {
      offset: { offset: 0 },
      flip: { enabled: true },
      preventOverflow: { boundariesElement: 'scrollParent' }
    }
  }
  const options = props.popperOpts || {}
  return {
    ...config,
    ...options,
    modifiers: { ...config.modifiers, ...(options.modifiers || {}) }
  }
}

const destroyPopper = () => {
  if(popper) popper.destroy()
  popper = null
}

const clearHideTimeout = () => {
  clearTimeout(hideTimeout)
  hideTimeout = null
}

// a click or focus outside the toggle and the menu closes the menu
const hideHandler = (event) => {
  const { target } = event
  if(visible.value && !menu.value.contains(target) && !toggler.value.contains(target)) {
    clearHideTimeout()
    hideTimeout = setTimeout(() => hide(), 0)
  }
}

const whileOpenListen = (isOpen) => {
  document.removeEventListener('click', hideHandler)
  document.removeEventListener('focusin', hideHandler)
  if(stopListeningToOthers) stopListeningToOthers()
  stopListeningToOthers = null
  if(isOpen) {
    document.addEventListener('click', hideHandler)
    document.addEventListener('focusin', hideHandler)
    // only one dropdown is open at a time
    stopListeningToOthers = onOtherDropdownOpened(id, () => {
      visible.value = false
    })
  }
}

const focusToggler = () => {
  nextTick(() => {
    if(toggler.value) toggler.value.focus()
  })
}

const showMenu = () => {
  destroyPopper()
  popper = new Popper(toggler.value, menu.value, popperConfig())
  dropdownOpened(id)
  whileOpenListen(true)
  nextTick(() => {
    if(menu.value) menu.value.focus()
    emit('shown')
  })
}

const hideMenu = () => {
  whileOpenListen(false)
  emit('hidden')
  if(focusTogglerOnHidden) focusToggler()
  focusTogglerOnHidden = false
  destroyPopper()
}

const show = () => {
  requestAnimationFrame(() => {
    visible.value = true
  })
}

const hide = (refocus = false) => {
  // "hidden" is emitted from the watcher, after this function returns
  if(refocus === true && visible.value) focusTogglerOnHidden = true
  visible.value = false
}

const stopEvent = (event, propagation = true) => {
  event.preventDefault()
  if(propagation) event.stopPropagation()
}

const toggle = (event) => {
  const { type, key } = event
  if(type !== 'click' && !(type === 'keydown' && ['Enter', ' ', 'ArrowDown'].includes(key))) return
  emit('toggle', event)
  stopEvent(event)
  if(visible.value) hide(true)
  else show()
}

// keeps the focus where it is until the click
const onMousedown = (event) => {
  stopEvent(event, false)
}

const items = () => {
  const selector = '.dropdown-item:not(.disabled):not([disabled]), .b-dropdown-form:not(.disabled):not([disabled])'
  return Array.from(menu.value.querySelectorAll(selector)).filter(el => el.offsetParent !== null || el.getClientRects().length > 0)
}

const focusNext = (event, up) => {
  const { target } = event
  if(!visible.value || target.closest('.dropdown form')) return
  stopEvent(event)
  nextTick(() => {
    const list = items()
    if(list.length < 1) return
    let index = list.indexOf(target)
    if(up && index > 0) index--
    else if(!up && index < list.length - 1) index++
    if(index < 0) index = 0
    list[index].focus()
  })
}

const onKeydown = (event) => {
  const { key } = event
  if(key === 'Escape') {
    if(visible.value) {
      focusTogglerOnHidden = true
      visible.value = false
      stopEvent(event)
    }
  } else if(key === 'ArrowDown') {
    focusNext(event, false)
  } else if(key === 'ArrowUp') {
    focusNext(event, true)
  }
}

watch(visible, (newValue, oldValue) => {
  if(visibleChangePrevented) {
    visibleChangePrevented = false
    return
  }
  if(newValue === oldValue) return
  const bvEvent = {
    type: newValue ? 'show' : 'hide',
    cancelable: true,
    defaultPrevented: false,
    target: menu.value,
    componentId: id,
    preventDefault() {
      this.defaultPrevented = true
    }
  }
  emit(bvEvent.type, bvEvent)
  if(bvEvent.defaultPrevented) {
    visibleChangePrevented = true
    visible.value = oldValue
    focusTogglerOnHidden = false
    return
  }
  if(newValue) showMenu()
  else hideMenu()
}, { flush: 'post' }) // after the menu is displayed, so that popper measures it at its real size

// items close the menu they are in
provide('bvDropdown', { hide })

onBeforeUnmount(() => {
  whileOpenListen(false)
  destroyPopper()
  clearHideTimeout()
})

// used by parent components through a ref (see the "public contract" test in bDropdown.test.js)
defineExpose({
  show,
  hide,
  visible,
})
</script>
